'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useDashboard } from '@/contexts/DashboardContext';

type AuthGuardProps = {
    children: React.ReactNode;
};

const LOGIN_PATH = '/login';
const DEFAULT_AUTHED_PATH = '/home';

/**
 * AuthGuard simplificado — consome a sessão centralizada do DashboardContext.
 * NÃO cria listener próprio de auth, evitando duplicação e race conditions.
 */
export function AuthGuard({ children }: AuthGuardProps) {
    const router = useRouter();
    const pathname = usePathname();
    const { session, isSessionLoading } = useDashboard();

    useEffect(() => {
        // Ainda carregando a sessão — não decidir nada
        if (isSessionLoading) return;

        const isLoginRoute = pathname === LOGIN_PATH;
        const hasSession = !!session?.user;

        if (isLoginRoute && hasSession) {
            // Já logado tentando acessar /login → redirecionar para home
            router.replace(DEFAULT_AUTHED_PATH);
            return;
        }

        if (!isLoginRoute && !hasSession) {
            // Não logado tentando acessar rota protegida → redirecionar para login
            router.replace(LOGIN_PATH);
            return;
        }
    }, [session, isSessionLoading, pathname, router]);

    // Exibir nada enquanto verifica sessão ou se precisa redirecionar
    if (isSessionLoading) return null;

    const isLoginRoute = pathname === LOGIN_PATH;
    const hasSession = !!session?.user;

    // Se estamos na rota errada, não renderizar (o useEffect redirecionará)
    if (isLoginRoute && hasSession) return null;
    if (!isLoginRoute && !hasSession) return null;

    return <>{children}</>;
}
